import React, {
  Fragment,
  Component,
  createRef,
  RefObject,
  ChangeEvent,
} from 'react'
import TextInput from 'tfs.form-text-input'

import NotificationInterface from '../../interfaces/client/Notification'
import ClientPassengerInterface from '../../interfaces/client/Passenger'

import { frontEndDateFormat } from '../../config/dates'
import { bookingIdMinLength, bookingIdMaxLength } from '../../config/forms'

import getLabels from '../../utils/getLabels'
import getNotificationLabelFromCode from '../../utils/notifications/getNotificationLabelFromCode'

import AddSSR from '../../containers/AddSSR'
import GenderSelect from '../../containers/GenderSelect'
import LuggageSelect from '../../containers/LuggageSelect'
import PassengerSSRs from '../../containers/PassengerSSRs'
import NewBookingFormInfantRow from '../../containers/NewBookingFormInfantRow'

import ToggleButton from '../ToggleButton'
import FormRowSeparator from '../FormRowSeparator'

import styles from './NewBookingFormRow.module.scss'

interface NewBookingFormRowProps {
  addSSR: Function
  removeNotification: Function
  dateValidator: Function
  validateDateOfBirth: Function

  rowId: number
  passengerId: string
  travelClass: string
  departureFlightId: string
  specialSSRNames: string[]

  showTravelClass: boolean
  showLuggage: boolean
  showSSRButton: boolean
  isDisabled: boolean

  firstNameError?: NotificationInterface
  lastNameError?: NotificationInterface
  dateOfBirthError?: NotificationInterface
  thirdPartyBookingNumberError?: NotificationInterface
  readonly?: string
  pnr?: string
  uploadedPassenger?: ClientPassengerInterface
}

interface NewBookingFormRowState {
  isAddSSRActive: boolean
  isAddInfantActive: boolean
  isDirty: boolean
}

export default class NewBookingFormRow extends Component<
  NewBookingFormRowProps,
  NewBookingFormRowState
> {
  state = {
    isAddSSRActive: false,
    isAddInfantActive: false,
    isDirty: false,
  }

  bookingIdRef: RefObject<HTMLInputElement> = createRef()
  firstNameRef: RefObject<HTMLInputElement> = createRef()
  lastNameRef: RefObject<HTMLInputElement> = createRef()
  dateOfBirthRef: RefObject<HTMLInputElement> = createRef()
  luggageRef: RefObject<HTMLSelectElement> = createRef()
  classRef: RefObject<HTMLInputElement> = createRef()

  private readonly _toggleAddInfant = () => {
    if (this.state.isAddInfantActive) {
      this.setState({ isAddInfantActive: false })
      this._onChange()
    } else {
      this.setState({ isAddInfantActive: true })
      this._setIsDirty()
    }
  }

  private readonly _setIsDirty = () => this.setState({ isDirty: true })
  private readonly _setIsClean = () => this.setState({ isDirty: false })

  private readonly _onChange = (notification?: NotificationInterface) => {
    const inputHasAValue =
      (this.bookingIdRef.current && this.bookingIdRef.current.value) ||
      (this.firstNameRef.current && this.firstNameRef.current.value) ||
      (this.lastNameRef.current && this.lastNameRef.current.value) ||
      (this.dateOfBirthRef.current && this.dateOfBirthRef.current.value) ||
      (this.luggageRef.current && this.luggageRef.current.value) ||
      (this.classRef.current && this.classRef.current.value)

    inputHasAValue ? this._setIsDirty() : this._setIsClean()

    if (notification) {
      this.props.removeNotification(notification)
    }
  }

  private readonly _toggleSSRInput = () =>
    this.setState({ isAddSSRActive: !this.state.isAddSSRActive })

  private readonly _onDateInputChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    this.props.validateDateOfBirth(
      event.currentTarget,
      this.props.passengerId,
      this.props.dateValidator,
      this.props.dateOfBirthError
    )
  }

  componentDidMount() {
    if (this.props.uploadedPassenger && this.props.uploadedPassenger.infant) {
      this.setState({ isAddInfantActive: true })
    }
  }

  componentDidUpdate(prevProps: NewBookingFormRowProps) {
    if (
      this.props.isDisabled !== prevProps.isDisabled ||
      this.props.readonly !== prevProps.readonly
    ) {
      this.setState({ isAddSSRActive: false })
    }
  }

  render() {
    const {
      passengerId,
      travelClass,
      showTravelClass,
      showLuggage,
      showSSRButton,
      readonly,
      pnr,
      isDisabled,
      addSSR,
      specialSSRNames,
      departureFlightId,
      thirdPartyBookingNumberError,
      firstNameError,
      lastNameError,
      dateOfBirthError,
      uploadedPassenger,
      rowId,
    } = this.props
    const { isDirty } = this.state

    return (
      <Fragment>
        <span>{rowId}</span>

        <span className={styles['input-default']}>
          <TextInput
            elementRef={this.bookingIdRef}
            name={`${passengerId}-booking-id`}
            data-element={`${rowId}-booking-id`}
            required={isDirty}
            onChange={() => this._onChange(thirdPartyBookingNumberError)}
            readOnly={readonly}
            disabled={isDisabled}
            minLength={bookingIdMinLength}
            maxLength={bookingIdMaxLength}
            error={getNotificationLabelFromCode(thirdPartyBookingNumberError)}
            defaultValue={
              uploadedPassenger && uploadedPassenger.thirdPartyBookingNumber
            }
          />
        </span>

        <span>{pnr}</span>

        <span className={styles['input-default']}>
          <TextInput
            elementRef={this.firstNameRef}
            name={`${passengerId}-first-name`}
            data-element={`${rowId}-first-name`}
            required={isDirty}
            onChange={() => this._onChange(firstNameError)}
            readOnly={readonly}
            disabled={isDisabled}
            error={getNotificationLabelFromCode(firstNameError)}
            defaultValue={uploadedPassenger && uploadedPassenger.firstName}
          />
        </span>

        <span
          className={styles['input-last-name']}
          data-element={`${rowId}-last-name-wrapper`}
        >
          <TextInput
            elementRef={this.lastNameRef}
            name={`${passengerId}-last-name`}
            data-element={`${rowId}-last-name`}
            required={isDirty}
            onChange={() => this._onChange(lastNameError)}
            readOnly={readonly}
            disabled={isDisabled}
            error={getNotificationLabelFromCode(lastNameError)}
            defaultValue={uploadedPassenger && uploadedPassenger.lastName}
          />
        </span>

        <span className={styles['input-date-of-birth']}>
          <TextInput
            elementRef={this.dateOfBirthRef}
            name={`${passengerId}-date-of-birth`}
            placeholder={frontEndDateFormat}
            pattern="\d{2}-\d{2}-\d{4}"
            data-element={`${rowId}-date-of-birth`}
            onChange={this._onDateInputChange}
            onBlur={this._onDateInputChange}
            readOnly={readonly}
            disabled={isDisabled}
            error={getNotificationLabelFromCode(dateOfBirthError)}
            data-uploadedvalue={
              uploadedPassenger && uploadedPassenger.dateOfBirth
            }
          />
        </span>

        <span className={styles['input-select']}>
          <GenderSelect
            inputName={`${passengerId}-gender`}
            refName={`${rowId}-gender`}
            isRequired={isDirty}
            isDisabled={isDisabled || Boolean(readonly)}
            defaultValue={(uploadedPassenger && uploadedPassenger.gender) || ''}
          />
        </span>

        {showLuggage && (
          <span className={styles['input-select']}>
            <LuggageSelect
              refName={`${rowId}-luggage`}
              passengerId={passengerId}
              elementRef={this.luggageRef}
              onChange={this._onChange}
              isDisabled={isDisabled || Boolean(readonly)}
              uploadedValue={
                uploadedPassenger && `5B${uploadedPassenger.luggage}`
              }
            />
          </span>
        )}

        {showTravelClass && (
          <Fragment>
            <span data-element="travel-class">{travelClass}</span>
            <span></span>
          </Fragment>
        )}

        <input
          type="hidden"
          ref={this.classRef}
          name={`${passengerId}-travel-class`}
          data-element={`${rowId}-travel-class`}
          defaultValue={travelClass}
        />

        {showSSRButton && (
          <span
            data-element="ssrs-wrapper"
            className={styles['toggleble-button']}
          >
            <ToggleButton
              isOpen={this.state.isAddSSRActive}
              addDataElement={`${rowId}-add-ssrs`}
              removeDataElement={`${rowId}-close-ssrs`}
              removeLabel={getLabels('close_ssr')}
              addLabel={getLabels('add_ssr')}
              handleClick={this._toggleSSRInput}
              isDisabled={isDisabled || Boolean(readonly)}
            />
          </span>
        )}

        <span className={styles['toggleble-button']}>
          <ToggleButton
            isOpen={this.state.isAddInfantActive}
            addDataElement={`${rowId}-add-infant-button`}
            removeDataElement={`${rowId}-remove-infant-button`}
            removeLabel={getLabels('remove_infant')}
            addLabel={getLabels('add_infant')}
            handleClick={this._toggleAddInfant}
            isDisabled={isDisabled || Boolean(readonly)}
          />
        </span>

        {this.state.isAddInfantActive && (
          <NewBookingFormInfantRow
            rowId={rowId}
            passengerId={passengerId}
            showTravelClass={showTravelClass}
            readonly={readonly}
            isDisabled={isDisabled}
            departureFlightId={departureFlightId}
          />
        )}

        <PassengerSSRs
          passengerId={passengerId}
          isDisabled={Boolean(readonly) || isDisabled}
          readonly={Boolean(readonly)}
        />

        {this.state.isAddSSRActive && !readonly && !isDisabled && (
          <div className={styles['add-SSR']}>
            <AddSSR
              name={`${rowId}-add-ssr-input`}
              onConfirm={(id: string) => {
                addSSR(passengerId, id, specialSSRNames)
              }}
            />
          </div>
        )}

        <FormRowSeparator />
      </Fragment>
    )
  }
}
